import { useState, useCallback, useRef } from "react";
import type {
  CanvasState,
  CanvasImage,
  TextBlock,
  DrawingPath,
  Point,
  ToolType,
} from "./types";

const STORY_ASPECT = 9 / 16;
const DEFAULT_WIDTH = 375;
const DEFAULT_HEIGHT = DEFAULT_WIDTH / STORY_ASPECT;

const initialCanvasState: CanvasState = {
  drawings: [],
  textBlocks: [],
  images: [],
  selectedTool: "draw",
  selectedElementId: null,
  selectedElementType: null,
  backgroundColor: "#ffffff",
  currentColor: "#000000",
  currentStrokeWidth: 4,
  currentFontSize: 24,
  currentFontFamily: "Vazirmatn, Tahoma, sans-serif",
};

export function useStoryCanvas() {
  const [state, setState] = useState<CanvasState>(initialCanvasState);

  const currentPathRef = useRef<DrawingPath | null>(null);
  const pinchStartRef = useRef<{ distance: number; image: CanvasImage } | null>(null);
  const dragStartRef = useRef<{ offsetX: number; offsetY: number; image: CanvasImage } | null>(null);
  const cornerDragRef = useRef<{ imageId: string; corner: string; startWidth: number; startHeight: number; accumDx: number; accumDy: number } | null>(null);
  const textDragRef = useRef<{ id: string; offsetX: number; offsetY: number } | null>(null);
  const textResizeRef = useRef<{ id: string; edge: "left" | "right"; startWidth: number; startX: number } | null>(null);
  const rafScheduledRef = useRef(false);

  const setTool = useCallback((tool: ToolType) => {
    setState((prev) => ({ ...prev, selectedTool: tool, selectedElementId: null, selectedElementType: null }));
  }, []);

  const setCurrentColor = useCallback((color: string) => {
    setState((prev) => ({ ...prev, currentColor: color }));
  }, []);

  const setCurrentStrokeWidth = useCallback((width: number) => {
    setState((prev) => ({ ...prev, currentStrokeWidth: width }));
  }, []);

  const setCurrentFontSize = useCallback((size: number) => {
    setState((prev) => ({ ...prev, currentFontSize: Math.max(12, Math.min(72, size)) }));
  }, []);

  const setBackgroundColor = useCallback((color: string) => {
    setState((prev) => ({ ...prev, backgroundColor: color }));
  }, []);

  const addDrawingPoint = useCallback((point: Point) => {
    if (state.selectedTool !== "draw") return;
    setState((prev) => {
      const path = prev.drawings[prev.drawings.length - 1];
      if (path && path.id === currentPathRef.current?.id) {
        return {
          ...prev,
          drawings: [
            ...prev.drawings.slice(0, -1),
            { ...path, points: [...path.points, point] },
          ],
        };
      }
      const newPath: DrawingPath = {
        id: `draw-${Date.now()}`,
        points: [point],
        color: prev.currentColor,
        strokeWidth: prev.currentStrokeWidth,
        tool: "pen",
      };
      currentPathRef.current = newPath;
      return { ...prev, drawings: [...prev.drawings, newPath] };
    });
  }, [state.selectedTool]);

  const endDrawing = useCallback(() => {
    currentPathRef.current = null;
  }, []);

  const addTextBlock = useCallback((at?: Point) => {
    const x = at?.x ?? DEFAULT_WIDTH / 2;
    const y = at?.y ?? DEFAULT_HEIGHT / 2;
    setState((prev) => {
      const newBlock: TextBlock = {
        id: `text-${Date.now()}`,
        text: "",
        x,
        y,
        fontSize: prev.currentFontSize,
        color: prev.currentColor,
        fontFamily: prev.currentFontFamily,
        textAlign: "center",
        rotation: 0,
        isSelected: true,
        width: 260,
        height: 56,
      };
      return {
        ...prev,
        textBlocks: [...prev.textBlocks.map((t) => ({ ...t, isSelected: false })), newBlock],
        selectedElementId: newBlock.id,
        selectedElementType: "text",
      };
    });
  }, []);

  const updateTextBlock = useCallback((id: string, updates: Partial<TextBlock>) => {
    setState((prev) => ({
      ...prev,
      textBlocks: prev.textBlocks.map((t) =>
        t.id === id ? { ...t, ...updates } : t
      ),
    }));
  }, []);

  const deleteTextBlock = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      textBlocks: prev.textBlocks.filter((t) => t.id !== id),
      selectedElementId: prev.selectedElementId === id ? null : prev.selectedElementId,
      selectedElementType: prev.selectedElementId === id ? null : prev.selectedElementType,
    }));
  }, []);

  const selectElement = useCallback((id: string | null, type: "text" | "image" | null) => {
    setState((prev) => ({
      ...prev,
      selectedElementId: id,
      selectedElementType: type,
      textBlocks: prev.textBlocks.map((t) => ({ ...t, isSelected: t.id === id })),
      images: prev.images.map((img) => ({ ...img, isSelected: img.id === id })),
    }));
  }, []);

  // Add image full-screen (cover canvas); user can drag and pinch to adjust
  const addImage = useCallback((src: string, naturalWidth: number, naturalHeight: number) => {
    const scale = Math.max(DEFAULT_WIDTH / naturalWidth, DEFAULT_HEIGHT / naturalHeight);
    const w = naturalWidth * scale;
    const h = naturalHeight * scale;
    setState((prev) => {
      const newImage: CanvasImage = {
        id: `img-${Date.now()}`,
        src,
        x: DEFAULT_WIDTH / 2,
        y: DEFAULT_HEIGHT / 2,
        width: w,
        height: h,
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        isSelected: false,
        naturalWidth,
        naturalHeight,
      };
      return {
        ...prev,
        images: [...prev.images.map((i) => ({ ...i, isSelected: false })), newImage],
        selectedElementId: null,
        selectedElementType: null,
      };
    });
  }, []);

  const updateImage = useCallback((id: string, updates: Partial<CanvasImage>) => {
    setState((prev) => ({
      ...prev,
      images: prev.images.map((img) =>
        img.id === id ? { ...img, ...updates } : img
      ),
    }));
  }, []);

  const deleteImage = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      images: prev.images.filter((i) => i.id !== id),
      selectedElementId: prev.selectedElementId === id ? null : prev.selectedElementId,
      selectedElementType: prev.selectedElementId === id ? null : prev.selectedElementType,
    }));
  }, []);

  const startImageDrag = useCallback((image: CanvasImage, canvasX: number, canvasY: number) => {
    dragStartRef.current = { offsetX: image.x - canvasX, offsetY: image.y - canvasY, image };
  }, []);

  const pendingImageDragRef = useRef<{ x: number; y: number } | null>(null);
  const moveImageDrag = useCallback(
    (canvasX: number, canvasY: number) => {
      const d = dragStartRef.current;
      if (!d) return;
      pendingImageDragRef.current = {
        x: canvasX + d.offsetX,
        y: canvasY + d.offsetY,
      };
      if (!rafScheduledRef.current) {
        rafScheduledRef.current = true;
        requestAnimationFrame(() => {
          rafScheduledRef.current = false;
          const pending = pendingImageDragRef.current;
          const dd = dragStartRef.current;
          if (pending && dd) {
            updateImage(dd.image.id, { x: pending.x, y: pending.y });
          }
          pendingImageDragRef.current = null;
        });
      }
    },
    [updateImage]
  );

  const endImageDrag = useCallback(() => {
    dragStartRef.current = null;
  }, []);

  const startPinch = useCallback((image: CanvasImage, distance: number) => {
    pinchStartRef.current = { distance, image };
  }, []);

  const pendingPinchDistanceRef = useRef<number | null>(null);
  const movePinch = useCallback((distance: number) => {
    const start = pinchStartRef.current;
    if (!start) return;
    pendingPinchDistanceRef.current = distance;
    if (!rafScheduledRef.current) {
      rafScheduledRef.current = true;
      requestAnimationFrame(() => {
        rafScheduledRef.current = false;
        const dist = pendingPinchDistanceRef.current;
        pendingPinchDistanceRef.current = null;
        const s = pinchStartRef.current;
        if (dist == null || !s) return;
        const scale = dist / s.distance;
        const minSize = 40;
        const maxSize = Math.max(DEFAULT_WIDTH, DEFAULT_HEIGHT) * 2.5;
        const newW = Math.max(minSize, Math.min(maxSize, s.image.width * scale));
        const newH = (s.image.height / s.image.width) * newW;
        updateImage(s.image.id, { width: newW, height: newH });
        pinchStartRef.current = { distance: dist, image: { ...s.image, width: newW, height: newH } };
      });
    }
  }, [updateImage]);

  const endPinch = useCallback(() => {
    pinchStartRef.current = null;
  }, []);

  const startCornerDrag = useCallback((imageId: string, corner: string, image: CanvasImage) => {
    cornerDragRef.current = {
      imageId,
      corner,
      startWidth: image.width,
      startHeight: image.height,
      accumDx: 0,
      accumDy: 0,
    };
  }, []);

  const moveCornerDrag = useCallback((deltaX: number, deltaY: number) => {
    const c = cornerDragRef.current;
    if (!c) return;
    c.accumDx += deltaX;
    c.accumDy += deltaY;
    let newW = c.startWidth;
    let newH = c.startHeight;
    if (c.corner.includes("e")) newW = Math.max(20, c.startWidth + c.accumDx);
    if (c.corner.includes("w")) newW = Math.max(20, c.startWidth - c.accumDx);
    if (c.corner.includes("s")) newH = Math.max(20, c.startHeight + c.accumDy);
    if (c.corner.includes("n")) newH = Math.max(20, c.startHeight - c.accumDy);
    updateImage(c.imageId, { width: newW, height: newH });
  }, [updateImage]);

  const endCornerDrag = useCallback(() => {
    cornerDragRef.current = null;
  }, []);

  const startTextDrag = useCallback((id: string, canvasX: number, canvasY: number) => {
    const t = state.textBlocks.find((x) => x.id === id);
    if (t) textDragRef.current = { id, offsetX: t.x - canvasX, offsetY: t.y - canvasY };
  }, [state.textBlocks]);

  const pendingTextDragRef = useRef<{ x: number; y: number } | null>(null);
  const moveTextDrag = useCallback(
    (canvasX: number, canvasY: number) => {
      const d = textDragRef.current;
      if (!d) return;
      pendingTextDragRef.current = { x: canvasX + d.offsetX, y: canvasY + d.offsetY };
      if (!rafScheduledRef.current) {
        rafScheduledRef.current = true;
        requestAnimationFrame(() => {
          rafScheduledRef.current = false;
          const pending = pendingTextDragRef.current;
          const dd = textDragRef.current;
          if (pending && dd) updateTextBlock(dd.id, { x: pending.x, y: pending.y });
          pendingTextDragRef.current = null;
        });
      }
    },
    [updateTextBlock]
  );

  const endTextDrag = useCallback(() => {
    textDragRef.current = null;
  }, []);

  const MIN_TEXT_WIDTH = 80;
  const MAX_TEXT_WIDTH = DEFAULT_WIDTH - 32;

  const startTextResize = useCallback((id: string, edge: "left" | "right", startWidth: number, startX: number) => {
    textResizeRef.current = { id, edge, startWidth, startX };
  }, []);

  const moveTextResize = useCallback(
    (canvasX: number) => {
      const r = textResizeRef.current;
      if (!r) return;
      const deltaX = canvasX - r.startX;
      const newWidth =
        r.edge === "right"
          ? Math.max(MIN_TEXT_WIDTH, Math.min(MAX_TEXT_WIDTH, r.startWidth + deltaX))
          : Math.max(MIN_TEXT_WIDTH, Math.min(MAX_TEXT_WIDTH, r.startWidth - deltaX));
      updateTextBlock(r.id, { width: newWidth });
    },
    [updateTextBlock]
  );

  const endTextResize = useCallback(() => {
    textResizeRef.current = null;
  }, []);

  const resetCanvas = useCallback(() => {
    setState(initialCanvasState);
    currentPathRef.current = null;
    pinchStartRef.current = null;
    dragStartRef.current = null;
    cornerDragRef.current = null;
    textDragRef.current = null;
    textResizeRef.current = null;
    pendingImageDragRef.current = null;
    pendingTextDragRef.current = null;
    pendingPinchDistanceRef.current = null;
  }, []);

  const deleteSelected = useCallback(() => {
    if (state.selectedElementId && state.selectedElementType === "text") {
      deleteTextBlock(state.selectedElementId);
    } else if (state.selectedElementId && state.selectedElementType === "image") {
      deleteImage(state.selectedElementId);
    }
  }, [state.selectedElementId, state.selectedElementType, deleteTextBlock, deleteImage]);

  return {
    state,
    dimensions: { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
    setTool,
    setCurrentColor,
    setCurrentStrokeWidth,
    setCurrentFontSize,
    setBackgroundColor,
    addDrawingPoint,
    endDrawing,
    addTextBlock,
    updateTextBlock,
    deleteTextBlock,
    selectElement,
    addImage,
    updateImage,
    deleteImage,
    startImageDrag,
    moveImageDrag,
    endImageDrag,
    startPinch,
    movePinch,
    endPinch,
    startCornerDrag,
    moveCornerDrag,
    endCornerDrag,
    startTextDrag,
    moveTextDrag,
    endTextDrag,
    startTextResize,
    moveTextResize,
    endTextResize,
    resetCanvas,
    deleteSelected,
  };
}
